import 'whatwg-fetch'

import {
  ApiConnectionException,
  createUrl,
  defaultUnauthorizedHandler,
  HTTP_UNAUTHORIZED,
  setToken
} from '@js/common/apiUtils'

async function validateResponse(
  response,
  handleUnauthorized,
  onApiConnectionError
) {
  const validResponseCodes = [200, 201, 204]

  if (response.status === 204 && response.statusText === 'No Content') {
    return true
  }

  let jsonResponse = await response.json()

  if (validResponseCodes.indexOf(response.status) === -1) {
    const payload =
      response.status >= 500 ? { error: 'Internal Server Error' } : jsonResponse
    const exception = new ApiConnectionException(response.status, payload)

    if (exception.statusCode === HTTP_UNAUTHORIZED) {
      handleUnauthorized(exception)
    }

    if (onApiConnectionError) {
      onApiConnectionError(exception)
    } else {
      throw exception
    }
  }

  if (response.status !== 204) {
    return jsonResponse
  }

  return true
}

async function perform(
  endpoint,
  onApiConnectionError,
  method,
  payload,
  additionalHeaders,
  unauthorizedHandler
) {
  let headers = new Headers({
    'Content-Type': 'application/json'
  })

  if (additionalHeaders) {
    Object.keys(additionalHeaders).map(header => {
      headers.set(header, additionalHeaders[header])
    })
  }
  setToken(headers)

  let options = {
    credentials: 'include',
    method: method || 'GET',
    headers
  }

  if (payload) {
    if (method !== 'GET') {
      if (/multipart/.test(headers.get('content-type'))) {
        headers.delete('content-type')
        let formBody = new FormData()

        Object.keys(payload).map(el => {
          formBody.append(el, payload[el])
        })

        options.body = formBody
      } else {
        options.body = JSON.stringify(payload)
      }
    } else {
      const params = { ...payload }
      const originalURL = window.location.href

      if (originalURL.split('preview')[1] == '=1') {
        params['hidden'] = { within: [0, 1] }
      }
      if (originalURL.indexOf('ab_config') > -1) {
        params['ab_config'] = originalURL.split('ab_config=')[1].split('&')[0]
      }

      endpoint = endpoint + QueryParams.paramsToQueryString(params)
    }
  }

  return validateResponse(
    await fetch(createUrl(endpoint), options).catch(onApiConnectionError),
    unauthorizedHandler,
    onApiConnectionError
  )
}

async function get(
  endpoint,
  payload,
  additionalHeaders,
  onApiConnectionError,
  onUnauthorized = defaultUnauthorizedHandler
) {
  return await perform(
    endpoint,
    onApiConnectionError,
    'GET',
    payload,
    additionalHeaders || null,
    onUnauthorized
  )
}

async function del(
  endpoint,
  onApiConnectionError,
  onUnauthorized = defaultUnauthorizedHandler
) {
  return await perform(
    endpoint,
    onApiConnectionError,
    'DELETE',
    null,
    null,
    onUnauthorized
  )
}

async function patch(
  endpoint,
  payload,
  additionalHeaders,
  onApiConnectionError,
  onUnauthorized = defaultUnauthorizedHandler
) {
  return await perform(
    endpoint,
    onApiConnectionError,
    'PATCH',
    payload,
    additionalHeaders,
    onUnauthorized
  )
}

async function post(
  endpoint,
  payload,
  additionalHeaders,
  onApiConnectionError,
  onUnauthorized = defaultUnauthorizedHandler
) {
  return await perform(
    endpoint,
    onApiConnectionError,
    'POST',
    payload,
    additionalHeaders,
    onUnauthorized
  )
}

export { ApiConnectionException, del, get, patch, post }

/**
 * Easy to use interface to parse, set and update queryParams as used by the API.
 */
export const QueryParams = {
  /**
   * Generates query strings from given params.
   *
   * @param params
   * @returns {string}
   */
  paramsToQueryString(params) {
    let finalQueryParams = []

    for (var key in params) {
      let value = params[key]
      if (!value) {
        continue
      }
      if (typeof value === 'object') {
        Object.keys(value).map(function (val, index) {
          if (isNaN(value[val]) && typeof value[val] != 'object') {
            delete value[val] //delete keys which value is NaN
          }
        })
      }

      // +++ range / gte / lte +
      if (
        value.hasOwnProperty('startsWith') &&
        value.hasOwnProperty('endsWith')
      ) {
        let first = value.startsWith,
          last = value.endsWith

        if (parseInt(first) > parseInt(last)) {
          ;[first, last] = [last, first]
        }

        finalQueryParams.push(`${key}__range=${first},${last}`)
      } else if (value.hasOwnProperty('startsWith')) {
        finalQueryParams.push(`${key}__range=${value['startsWith']},`)
      } else if (value.hasOwnProperty('endsWith')) {
        finalQueryParams.push(`${key}__range=,${value['endsWith']}`)
      }
      // +++ in +
      else if (value.hasOwnProperty('within')) {
        let within = Array.isArray(value.within)
          ? value.within.join(',')
          : value.within

        if (within) {
          finalQueryParams.push(`${key}__in=${within}`)
        }
      }
      // +++ radius +
      else if (
        value.hasOwnProperty('distance') &&
        value.hasOwnProperty('latitude') &&
        value.hasOwnProperty('longitude')
      ) {
        finalQueryParams.push(
          `radius=${value.distance},${value.latitude},${value.longitude}`
        )
      }
      // +++ distance +
      else if (
        value.hasOwnProperty('latitude') &&
        value.hasOwnProperty('longitude')
      ) {
        finalQueryParams.push(`distance=${value.latitude},${value.longitude}`)
      }
      //object is empty
      else if (Object.keys(value).length == 0 && isNaN(value)) {
        continue
      } else {
        finalQueryParams.push(`${key}=${value}`)
      }
    }
    return finalQueryParams.length > 0 ? '?' + finalQueryParams.join('&') : ''
  },

  /**
   * JavaScriptified Object of the QueryParams.
   *
   * If params are given, they are used instead of the current URL.
   *
   * Translations:
   *    foo_range=1,2 -> params[foo]{startsWith: 1, endsWith2}
   *    foo_gte=1 -> params[foo]{startsWith: 1}
   *    foo_lte=2 -> params[foo]{endsWith: 2}
   * @returns {{}}
   */
  get(q) {
    var hash,
      params = {}
    q = q || window.location.search
    q = q.slice(1)

    function setOrDefault(key, param, value) {
      if (!params.hasOwnProperty(key)) {
        params[key] = {}
      }
      params[key][param] = value
    }

    if (q != undefined) {
      q = q.split('&')

      for (var i = 0; i < q.length; i++) {
        hash = q[i].split('=')

        if (hash.length < 2 || hash[1].length === 0) {
          continue
        }

        var key = hash[0],
          item = hash[1].split(',')
        if (item.length === 1) {
          item = item[0]
        }

        // range filter are parsed into a startsWith / endsWith object
        if (key.endsWith('__range')) {
          key = key.replace('__range', '')
          params[key] = {
            startsWith: parseInt(item[0])
          }

          if (item.length > 1) {
            params[key]['endsWith'] = parseInt(item[1])
          }
          continue
        }
        // lte and gte are parsed into startsWith / endsWith as well
        if (key.endsWith('__lte')) {
          key = key.replace('__lte', '')
          setOrDefault(key, 'endsWith', parseInt(item))
          continue
        }
        if (key.endsWith('__gte')) {
          key = key.replace('__gte', '')
          setOrDefault(key, 'startsWith', parseInt(item))
          continue
        }

        // in filter
        if (key.endsWith('__in') || key.endsWith('__within')) {
          key = key.replace('__in', '')
          key = key.replace('__within', '')

          if (!Array.isArray(item)) {
            if (item.indexOf(',') !== -1) {
              item = item.split(',')
            } else {
              item = [item]
            }
          }

          params[key] = {
            within: item.map(i => {
              return i
            })
          }
          continue
        }

        // radius filter
        if (key === 'radius' && item.length === 3) {
          params[key] = {
            distance: parseInt(item[0]),
            latitude: parseFloat(item[1]),
            longitude: parseFloat(item[2])
          }
          continue
        }

        if (key === 'distance' && item.length == 2) {
          params[key] = {
            latitude: parseFloat(item[0]),
            longitude: parseFloat(item[1])
          }
          continue
        }

        if (key.indexOf('__') === -1) {
          params[key] = item
        }
      }
    }
    return params
  },

  /**
   * Updates the url with the given query params
   * @param params
   * @returns {QueryParams}
   */
  set(params) {
    let paramsAsString = this.paramsToQueryString(params)

    if (window.history) {
      var update =
        window.location.protocol +
        '//' +
        window.location.host +
        window.location.pathname +
        paramsAsString
      window.history.replaceState(
        {
          path: update
        },
        '',
        update
      )
    }
  }
}
